import { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';

import B3Request from '@/shared/service/request/b3Fetch';

interface NavItem {
  entityId: number;
  name: string;
  path: string;
}

const GET_CATEGORY_TREE = `
  query GetCategoryTree {
    site {
      categoryTree {
        entityId
        name
        path
      }
    }
  }
`;

const GET_NAV_PAGES = `
  query GetNavPages {
    site {
      content {
        pages(first: 20) {
          edges {
            node {
              entityId
              name
              path
            }
          }
        }
      }
    }
  }
`;

export default function StorefrontNavBar() {
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    // Fetch categories first (known working), then try to add pages
    B3Request.graphqlBCProxy<{ data?: { site?: { categoryTree?: NavItem[] } } }>({
      query: GET_CATEGORY_TREE,
    })
      .then((res) => {
        const categories = res?.data?.site?.categoryTree ?? [];
        setNavItems(categories);

        // Attempt to append web pages — ignore if the query fails
        return B3Request.graphqlBCProxy<{
          data?: { site?: { content?: { pages?: { edges?: Array<{ node: NavItem }> } } } };
        }>({ query: GET_NAV_PAGES })
          .then((pageRes) => {
            const pages = (pageRes?.data?.site?.content?.pages?.edges ?? []).map((e) => e.node);
            if (pages.length > 0) {
              setNavItems([...categories, ...pages]);
            }
          })
          .catch(() => {});
      })
      .catch(() => {});
  }, []);

  if (navItems.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '4px',
        py: '8px',
        mb: '8px',
      }}
    >
      {navItems.map((item) => (
        <Button
          key={item.entityId}
          sx={{
            fontSize: '13px',
            fontWeight: 700,
            color: '#333333',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            minWidth: 'unset',
            px: '12px',
            '&:hover': { color: '#000000', backgroundColor: 'transparent' },
          }}
          onClick={() => {
            window.location.href = item.path;
          }}
        >
          {item.name}
        </Button>
      ))}
    </Box>
  );
}
